import { useState } from "react";
import { MenuItem, ListItemIcon, ListItemText, CircularProgress, Menu } from "@mui/material";
import {
    ContentCopy,
    Person as PersonIcon,
    Edit,
    Schedule,
    Note,
    Delete,
    DesignServices,
    SelectAll,
    ClearAll,
    MeetingRoom,
    Business,
    SwapHoriz,
    ArrowRight,
} from "@mui/icons-material";
import { UseFormReturn } from "react-hook-form";
import { CustomEventType } from "../../utils/types";
import { ActionMode, ScheduleActionFormData } from "./types";
import { timeOffTypes } from "../../utils/scheduleUtils";

type ConvertToMenuItemProps = {
    disabled: boolean;
    isConverting: boolean;
    onConvertToTimeOff: (timeOffType: string) => void;
};

// "Converter para..." opens a sub-menu of time-off types; picking one converts every selected schedule
// event into a time_off entry of that type (same hours) and deletes the original schedule.
const ConvertToMenuItem = ({ disabled, isConverting, onConvertToTimeOff }: ConvertToMenuItemProps) => {
    const [submenuAnchor, setSubmenuAnchor] = useState<null | HTMLElement>(null);

    return (
        <>
            <MenuItem
                onClick={(event) => setSubmenuAnchor(event.currentTarget)}
                disabled={disabled || isConverting}
                sx={{ pl: 1 }}
            >
                <ListItemIcon sx={{ minWidth: 36 }}>
                    {isConverting ? <CircularProgress size={16} sx={{ ml: 1 }} /> : <SwapHoriz color="primary" fontSize="small" />}
                </ListItemIcon>
                <ListItemText>Converter para...</ListItemText>
                <ArrowRight fontSize="small" color="action" />
            </MenuItem>
            <Menu
                anchorEl={submenuAnchor}
                open={Boolean(submenuAnchor)}
                onClose={() => setSubmenuAnchor(null)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                {timeOffTypes.map((timeOffType) => (
                    <MenuItem
                        key={timeOffType.value}
                        onClick={() => {
                            setSubmenuAnchor(null);
                            onConvertToTimeOff(timeOffType.value);
                        }}
                    >
                        {timeOffType.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

type MenuItemsProps = {
    selectedEvents: Array<CustomEventType>;
    setSelectedEvents: (events: Array<CustomEventType>) => void;
    onActionModeChange: (mode: ActionMode, isUpdateMode?: boolean) => void;
    onCopyEvents: () => void;

    // from parent's useScheduleActions
    formMethods: UseFormReturn<ScheduleActionFormData>;
    watchedValues: ScheduleActionFormData;
    isWeekModeActive: boolean;
    isCopying: boolean;
    isDeleting: boolean;
    isConverting: boolean;
    handleSelectAll: () => void;
    handleDelete: () => Promise<void>;
    handleConvertToTimeOff: (timeOffType: string) => void;
};

export const MenuItems = ({
    selectedEvents,
    setSelectedEvents,
    onActionModeChange,
    onCopyEvents,
    watchedValues,
    isWeekModeActive,
    isCopying,
    isDeleting,
    isConverting,
    handleSelectAll,
    handleDelete,
    handleConvertToTimeOff,
}: MenuItemsProps) => {
    const isEventSelected = selectedEvents.length > 0;
    const selectedWeekLength = watchedValues.selectedWeek.length;

    const menuItems = [
        <MenuItem
            key="copy"
            onClick={() => onCopyEvents()}
            disabled={!isEventSelected || (isWeekModeActive && selectedWeekLength === 0)}
            sx={{ pl: 1 }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>
                {isCopying ? (
                    <CircularProgress size={16} sx={{ ml: 1 }} />
                ) : (
                    <ContentCopy color="primary" fontSize="small" />
                )}
            </ListItemIcon>
            <ListItemText>
                {isWeekModeActive
                    ? `Copiar para ${selectedWeekLength} semana${
                          selectedWeekLength > 1 ? "s" : ""
                      } selecionada${selectedWeekLength > 1 ? "s" : ""}`
                    : "Copiar com o mesmo técnico"}
            </ListItemText>
        </MenuItem>,
        !isWeekModeActive && (
            <MenuItem
                key="different-tech"
                onClick={() => {
                    onActionModeChange("copy", false);
                }}
                disabled={selectedEvents.length === 0}
                sx={{ pl: 1 }}
            >
                <ListItemIcon sx={{ minWidth: 36 }}>
                    <PersonIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copiar com técnico diferente</ListItemText>
            </MenuItem>
        ),
        <MenuItem
            key="update-tech"
            onClick={() => {
                onActionModeChange("update", true);
            }}
            disabled={!isEventSelected}
            sx={{ pl: 1 }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>
                <Edit color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>Atualizar técnico</ListItemText>
        </MenuItem>,
        <MenuItem
            key="update-services"
            onClick={() => onActionModeChange("services", false)}
            disabled={!isEventSelected}
            sx={{ pl: 1 }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>
                <DesignServices color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>Atualizar serviços</ListItemText>
        </MenuItem>,
        <MenuItem
            key="update-room"
            onClick={() => onActionModeChange("room", false)}
            disabled={!isEventSelected}
            sx={{ pl: 1 }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>
                <MeetingRoom color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>Atualizar sala</ListItemText>
        </MenuItem>,
        <MenuItem
            key="update-clinic"
            onClick={() => onActionModeChange("clinic", false)}
            disabled={!isEventSelected}
            sx={{ pl: 1 }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>
                <Business color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>Atualizar clínica</ListItemText>
        </MenuItem>,
        <MenuItem
            key="change-times"
            onClick={() => onActionModeChange("time", false)}
            disabled={!isEventSelected}
            sx={{ pl: 1 }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>
                <Schedule color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>Alterar horários</ListItemText>
        </MenuItem>,
        <MenuItem
            key="update-notes"
            onClick={() => onActionModeChange("notes", false)}
            disabled={!isEventSelected}
            sx={{ pl: 1 }}
        >
            <ListItemIcon sx={{ minWidth: 36 }}>
                <Note color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>Atualizar notas</ListItemText>
        </MenuItem>,
        <ConvertToMenuItem
            key="convert-to-timeoff"
            disabled={!isEventSelected}
            isConverting={isConverting}
            onConvertToTimeOff={handleConvertToTimeOff}
        />,
        <MenuItem key="delete" onClick={handleDelete} disabled={!isEventSelected || isDeleting} sx={{ pl: 1 }}>
    <ListItemIcon sx={{ minWidth: 36 }}>
        {isDeleting ? (
            <CircularProgress size={16} sx={{ ml: 1 }} />
        ) : (
            <Delete color="error" fontSize="small" />
        )}
    </ListItemIcon>
    <ListItemText>Apagar eventos selecionados</ListItemText>
</MenuItem>,
        <MenuItem key="select-all" onClick={handleSelectAll} sx={{ pl: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
                <SelectAll color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>Selecionar todos os eventos</ListItemText>
        </MenuItem>,
        <MenuItem key="clear-select-all" onClick={() => setSelectedEvents([])} sx={{ pl: 1 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
                <ClearAll color="primary" fontSize="small" />
            </ListItemIcon>
            <ListItemText>Limpar seleção de eventos</ListItemText>
        </MenuItem>,
    ];

    return <>{menuItems.filter(Boolean)}</>;
}; 